import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { get } from '../../helpers/api_helper';
import SevenSegmentDigit from './SevenSegmentDigit';

const YIELD_WARNING_MARGIN = 0.1;

function formatRate(value) {
  const rate = Number(value);
  if (!Number.isFinite(rate)) return '—';

  return new Intl.NumberFormat([], {
    maximumFractionDigits: 2,
  }).format(Object.is(rate, -0) ? 0 : rate);
}

function getVisibleTargetOrders(comparison) {
  if (comparison?.status === 'ahead') {
    const { orders_to_next_target: nextTargetValue } = comparison;
    const nextTargetOrders = Number(nextTargetValue);
    if (nextTargetValue !== null && Number.isFinite(nextTargetOrders)) return nextTargetOrders;
  }

  const targetValue = comparison?.orders_needed;
  const targetOrders = Number(targetValue);
  return targetValue !== null && targetValue !== undefined && Number.isFinite(targetOrders)
    ? targetOrders
    : null;
}

function getTargetYield(comparison, now) {
  const orders = getVisibleTargetOrders(comparison);
  if (orders === null) return null;

  const targetAt = new Date(now);
  const firstBenchmarkValue = comparison?.benchmark_hour;
  const firstBenchmarkHour = Number(firstBenchmarkValue);
  if (comparison?.status === 'pending'
    && firstBenchmarkValue !== null
    && firstBenchmarkValue !== undefined
    && Number.isFinite(firstBenchmarkHour)) {
    targetAt.setHours(firstBenchmarkHour, 0, 0, 0);
  } else {
    targetAt.setMinutes(60, 0, 0);
  }
  const minutesRemaining = (targetAt.getTime() - now.getTime()) / 60000;

  return {
    perMinute: orders / minutesRemaining,
    targetAt,
  };
}

function getYieldStatus(currentYield, targetYield) {
  if (!Number.isFinite(currentYield) || !Number.isFinite(targetYield)) return 'neutral';
  if (currentYield >= targetYield) return 'positive';
  if (targetYield - currentYield <= YIELD_WARNING_MARGIN) return 'warning';

  return 'negative';
}

function formatClockDate(date) {
  const day = date.getDate();
  const remainder = day % 100;
  const suffix = remainder >= 11 && remainder <= 13
    ? 'th'
    : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
  const weekday = date.toLocaleDateString([], { weekday: 'short' });
  const month = date.toLocaleDateString([], { month: 'short' });

  return `${weekday}, ${month} ${day}${suffix} ${date.getFullYear()}`;
}

function ClockSeparator({ small }) {
  return (
    <span className={`today-clock-separator${small ? ' today-clock-separator--small' : ''}`} aria-hidden="true">
      <i />
      <i />
    </span>
  );
}

ClockSeparator.defaultProps = {
  small: false,
};

ClockSeparator.propTypes = {
  small: PropTypes.bool,
};

function ClockWidget({ refreshKey, t }) {
  const [now, setNow] = useState(() => new Date());
  const [flowRates, setFlowRates] = useState(null);
  const [flowComparison, setFlowComparison] = useState(null);
  const [flowError, setFlowError] = useState(false);

  useEffect(() => {
    setNow(new Date());
  }, [refreshKey]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadFlowRates() {
      try {
        const response = await get('/api/today/awaiting-orders');
        if (mounted) {
          const rates = response.flow_rates || null;
          setFlowRates(rates);
          setFlowComparison(response.comparison || null);
          setFlowError(rates === null);
        }
      } catch (requestError) {
        if (mounted) setFlowError(true);
      }
    }

    loadFlowRates();
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const rawHours = now.getHours();
  const hours = String(rawHours % 12 || 12);
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const accessibleTime = now.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
  const rateValue = (key) => {
    if (flowError) return '—';
    if (!flowRates) return '…';

    return formatRate(flowRates[key]);
  };
  const yieldRate = flowRates ? Number(flowRates.yield_per_minute) : Number.NaN;
  const targetYield = getTargetYield(flowComparison, now);
  const yieldStatus = getYieldStatus(yieldRate, targetYield?.perMinute);
  const targetTime = targetYield?.targetAt.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div className="today-clock-card" aria-label={`Current time ${accessibleTime}`} role="timer">
      <div className="today-clock-face">
        <div className="today-clock-primary">
          {hours.split('').map((digit, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SevenSegmentDigit key={`hour-${index}`} value={digit} />
          ))}
          <ClockSeparator />
          {minutes.split('').map((digit, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SevenSegmentDigit key={`minute-${index}`} value={digit} />
          ))}
        </div>
        <div className="today-clock-secondary">
          <div className="today-clock-seconds">
            {seconds.split('').map((digit, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <SevenSegmentDigit key={`second-${index}`} value={digit} small />
            ))}
          </div>
        </div>
      </div>
      <div className="today-clock-date">
        {formatClockDate(now)}
      </div>
      <div className="today-clock-flow">
        <div className="today-clock-flow__factors">
          <div className="today-clock-flow__factor">
            <span>{t('Orders in / min')}</span>
            <strong>{rateValue('orders_in_per_minute')}</strong>
          </div>
          <div className="today-clock-flow__factor">
            <span>{t('Orders out / min')}</span>
            <strong>{rateValue('orders_out_per_minute')}</strong>
            <small>{t('Shipped / voided')}</small>
          </div>
        </div>
        <div
          className={`today-clock-flow__yield today-clock-flow__yield--${yieldStatus}`}
        >
          <span>{t('Yield / min')}</span>
          <strong>{rateValue('yield_per_minute')}</strong>
          <div className="today-clock-flow__target">
            <span>{t('Target')}</span>
            <strong>{targetYield ? formatRate(targetYield.perMinute) : '…'}</strong>
            <small>{t('/ min by {{time}}', { time: targetTime || '—' })}</small>
          </div>
        </div>
      </div>
    </div>
  );
}

ClockWidget.defaultProps = {
  t: (value) => value,
};

ClockWidget.propTypes = {
  refreshKey: PropTypes.number.isRequired,
  t: PropTypes.func,
};

export default withTranslation()(ClockWidget);
